import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import PageShell from "../../components/layout/PageShell";
import {
    createBillingCheckout,
    createBillingPortal,
    getCurrentBillingHistory,
    getCurrentBillingUsage
} from "../../services/crmService";
import { getSession } from "../../services/authService";

import "../Setting.css";

const PLAN_OPTIONS = [
    { key: "starter", label: "Starter" },
    { key: "growth", label: "Growth" },
    { key: "agency", label: "Agency" },
    { key: "enterprise", label: "Enterprise" }
];

function formatMoney(amount, currency = "INR") {
    const numeric = Number(amount || 0) / 100;

    try {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: currency || "INR",
            maximumFractionDigits: 2
        }).format(numeric);
    } catch {
        return `${currency || "INR"} ${numeric.toFixed(2)}`;
    }
}

function formatDate(value) {
    if (!value) return "—";

    try {
        return new Intl.DateTimeFormat("en-IN", {
            dateStyle: "medium",
            timeStyle: "short"
        }).format(new Date(value));
    } catch {
        return "—";
    }
}

function normalizeStatus(value) {
    return String(value || "unknown")
        .replace(/_/g, " ")
        .trim();
}

function UsageMetric({ label, value, description }) {
    return (
        <div className="client-usage-card">
            <span>{label}</span>
            <strong>{value ?? 0}</strong>
            <small>{description}</small>
        </div>
    );
}

function SettingHelp({ title, children }) {
    return (
        <div className="client-setting-help">
            <strong>{title}</strong>
            <span>{children}</span>
        </div>
    );
}

function BillingStatusPill({ status }) {
    const clean = String(status || "unknown").toLowerCase();

    let tone = "neutral";

    if (["active", "paid", "captured", "authenticated"].includes(clean)) {
        tone = "success";
    } else if (["trialing", "created", "pending"].includes(clean)) {
        tone = "info";
    } else if (["past_due", "failed", "halted"].includes(clean)) {
        tone = "warning";
    } else if (["canceled", "cancelled", "suspended"].includes(clean)) {
        tone = "danger";
    }

    return (
        <span className={`billing-status-pill ${tone}`}>
            {normalizeStatus(status)}
        </span>
    );
}

function InvoiceRow({ invoice }) {
    const url = invoice.invoicePdfUrl || invoice.hostedInvoiceUrl;

    return (
        <div className="billing-invoice-row">
            <div>
                <strong>
                    {invoice.providerInvoiceId || invoice.id || "Invoice"}
                </strong>
                <span>
                    {invoice.provider || "razorpay"} · {formatDate(invoice.invoiceCreatedAtUtc || invoice.createdAtUtc)}
                </span>
                <small>
                    Payment: {invoice.providerPaymentIntentId || "—"}
                </small>
            </div>

            <div className="billing-invoice-amount">
                <strong>
                    {formatMoney(invoice.amountPaid || invoice.amountDue, invoice.currency)}
                </strong>
                <BillingStatusPill status={invoice.status} />
            </div>

            <div className="billing-invoice-actions">
                {url ? (
                    <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="project-action-btn secondary compact"
                    >
                        Open Invoice
                    </a>
                ) : (
                    <span className="billing-muted-note">
                        Receipt recorded
                    </span>
                )}
            </div>
        </div>
    );
}

function SubscriptionCard({ subscription }) {
    if (!subscription) {
        return (
            <div className="billing-empty-card">
                <strong>No Razorpay subscription found</strong>
                <span>
                    Start checkout to create a Razorpay subscription for this billing account.
                </span>
            </div>
        );
    }

    return (
        <div className="billing-subscription-card">
            <div>
                <span className="project-kicker">Razorpay Subscription</span>
                <h2>{subscription.planKey || "Plan"}</h2>
                <p>
                    Subscription ID: <strong>{subscription.providerSubscriptionId || "—"}</strong>
                </p>
            </div>

            <div className="billing-subscription-meta">
                <BillingStatusPill status={subscription.status} />
                <span>Current period start: {formatDate(subscription.currentPeriodStartUtc)}</span>
                <span>Current period end: {formatDate(subscription.currentPeriodEndUtc)}</span>
                {subscription.cancelAtPeriodEnd && <span>Cancel at period end</span>}
            </div>
        </div>
    );
}

export default function ClientUsageSettingsPage() {
    const session = getSession() || {};

    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [portalLoading, setPortalLoading] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState("starter");

    const [usage, setUsage] = useState(null);
    const [history, setHistory] = useState(null);

    const billingContext = session.billingContext || {};
    const isAgencyClient =
        billingContext.isAgencyClient === true ||
        (
            String(session.accountType || "").toLowerCase() === "client" &&
            !!session.parentAccountId &&
            !!session.billingOwnerAccountId &&
            session.billingOwnerAccountId !== session.accountId
        );

    const invoiceProvider = isAgencyClient
        ? billingContext.billingDisplayName || session.billingOwnerDisplayBrandName || session.parentDisplayBrandName || "your agency"
        : "Shriansh Nexus";

    const latestSubscription = useMemo(() => {
        const items = history?.subscriptions || history?.Subscriptions || [];
        return Array.isArray(items) && items.length > 0 ? items[0] : null;
    }, [history]);

    const invoices = useMemo(() => {
        const items = history?.invoices || history?.Invoices || [];
        return Array.isArray(items) ? items : [];
    }, [history]);

    const customers = useMemo(() => {
        const items = history?.customers || history?.Customers || [];
        return Array.isArray(items) ? items : [];
    }, [history]);

    const checkoutSessions = useMemo(() => {
        const items = history?.checkoutSessions || history?.CheckoutSessions || [];
        return Array.isArray(items) ? items : [];
    }, [history]);

    const currentPlan =
        history?.billingPlanKey ||
        history?.BillingPlanKey ||
        usage?.planKey ||
        usage?.billingPlanKey ||
        session.billingPlanKey ||
        "trial";

    const subscriptionStatus =
        history?.subscriptionStatus ||
        history?.SubscriptionStatus ||
        usage?.subscriptionStatus ||
        session.subscriptionStatus ||
        "trialing";

    const loadBilling = async () => {
        try {
            setLoading(true);

            const [usageData, historyData] = await Promise.all([
                getCurrentBillingUsage().catch((error) => {
                    console.warn("Billing usage failed", error);
                    return null;
                }),
                getCurrentBillingHistory().catch((error) => {
                    console.warn("Billing history failed", error);
                    return null;
                })
            ]);

            setUsage(usageData);
            setHistory(historyData);

            const plan =
                historyData?.billingPlanKey ||
                historyData?.BillingPlanKey ||
                usageData?.planKey ||
                usageData?.billingPlanKey ||
                session.billingPlanKey ||
                "starter";

            setSelectedPlan(String(plan || "starter").toLowerCase());
        } catch (error) {
            console.error(error);
            toast.error("Failed to load billing details.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBilling();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCheckout = async () => {
        if (isAgencyClient) {
            toast.error(`Billing is managed by ${invoiceProvider}.`);
            return;
        }

        try {
            setCheckoutLoading(true);
            const result = await createBillingCheckout(selectedPlan);

            if (!result?.success && !result?.Success) {
                toast.error(result?.message || result?.Message || "Unable to start Razorpay checkout.");
                return;
            }

            const subscriptionId = result.subscriptionId || result.SubscriptionId;
            const keyId = result.keyId || result.KeyId;

            toast.success("Razorpay subscription created.");

            if (subscriptionId && keyId && window.Razorpay) {
                const options = {
                    key: keyId,
                    subscription_id: subscriptionId,
                    name: "Shriansh Nexus",
                    description: `${selectedPlan} subscription`,
                    handler: () => {
                        toast.success("Payment completed. Billing status will update shortly.");
                        loadBilling();
                    },
                    modal: {
                        ondismiss: () => {
                            toast("Checkout closed.");
                        }
                    }
                };

                const razorpay = new window.Razorpay(options);
                razorpay.open();
            } else {
                await loadBilling();
            }
        } catch (error) {
            console.error(error);
            toast.error(
                error?.response?.data?.message ||
                error?.response?.data?.Message ||
                "Failed to start checkout."
            );
        } finally {
            setCheckoutLoading(false);
        }
    };

    const handlePortal = async () => {
        if (isAgencyClient) {
            toast.error(`Billing is managed by ${invoiceProvider}.`);
            return;
        }

        try {
            setPortalLoading(true);
            const result = await createBillingPortal();

            if (result?.url || result?.Url) {
                window.open(result.url || result.Url, "_blank", "noopener,noreferrer");
                return;
            }

            toast(
                result?.message ||
                result?.Message ||
                "Razorpay portal is not enabled. Manage subscription from Razorpay dashboard."
            );
        } catch (error) {
            console.error(error);
            toast.error(
                error?.response?.data?.message ||
                error?.response?.data?.Message ||
                "Billing portal is not available."
            );
        } finally {
            setPortalLoading(false);
        }
    };

    if (loading) {
        return (
            <PageShell title="Billing, Usage & Plan">
                <div className="project-loading-card">Loading billing details...</div>
            </PageShell>
        );
    }

    return (
        <PageShell title="Billing, Usage & Plan">
            <div className="settings-shell client-settings-shell billing-settings-shell">
                <section className="client-settings-hero">
                    <div>
                        <span className="project-kicker">Billing Center</span>
                        <h1>Billing, Invoices & Usage</h1>
                        <p>
                            Review Razorpay subscription status, invoices, usage, and billing ownership for this workspace.
                        </p>

                        <div className="client-settings-summary">
                            <span>{currentPlan} plan</span>
                            <span>{subscriptionStatus}</span>
                            <span>Invoice provider: {invoiceProvider}</span>
                        </div>
                    </div>

                    <div className="billing-hero-actions">
                        <button
                            type="button"
                            className="project-action-btn secondary"
                            onClick={loadBilling}
                        >
                            Refresh
                        </button>

                        {!isAgencyClient && (
                            <button
                                type="button"
                                className="project-action-btn primary"
                                onClick={handleCheckout}
                                disabled={checkoutLoading}
                            >
                                {checkoutLoading ? "Starting..." : "Upgrade / Pay"}
                            </button>
                        )}
                    </div>
                </section>

                {isAgencyClient && (
                    <section className="project-panel client-setting-panel billing-managed-card">
                        <span className="project-kicker">Agency Billing</span>
                        <h2>Billing is managed by {invoiceProvider}</h2>
                        <p className="settings-muted">
                            This client workspace is billed through the agency account. Please contact the agency for invoices, payment changes, or plan upgrades.
                        </p>

                        <div className="billing-context-grid">
                            <div>
                                <small>Billing owner</small>
                                <strong>{invoiceProvider}</strong>
                            </div>
                            <div>
                                <small>Support email</small>
                                <strong>
                                    {billingContext.billingSupportEmail ||
                                        session.billingOwnerSupportEmail ||
                                        session.parentSupportEmail ||
                                        "Contact agency"}
                                </strong>
                            </div>
                            <div>
                                <small>Workspace plan</small>
                                <strong>{currentPlan}</strong>
                            </div>
                        </div>
                    </section>
                )}

                <section className="client-usage-grid">
                    <UsageMetric
                        label="Projects"
                        value={usage?.projectCount ?? usage?.ProjectCount ?? 0}
                        description="Generated website projects connected to this account."
                    />

                    <UsageMetric
                        label="AI Calls"
                        value={usage?.aiCalls ?? usage?.AiCalls ?? 0}
                        description="AI text/image operations recorded for this account."
                    />

                    <UsageMetric
                        label="Deployments"
                        value={usage?.deploymentCount ?? usage?.DeploymentCount ?? 0}
                        description="Website publish, redeploy, or recovery actions."
                    />

                    <UsageMetric
                        label="CRM Items"
                        value={usage?.crmItemCount ?? usage?.CrmItemCount ?? 0}
                        description="Programs, products, listings, testimonials, and module items."
                    />

                    <UsageMetric
                        label="Leads"
                        value={usage?.leadCount ?? usage?.LeadCount ?? 0}
                        description="Website enquiries captured inside the CRM."
                    />
                </section>

                <section className="client-settings-layout">
                    <div className="client-settings-main">
                        <div className="project-panel client-setting-panel">
                            <span className="project-kicker">Current Plan</span>
                            <h2>Plan & Subscription</h2>
                            <p className="settings-muted">
                                Razorpay subscription and account billing status for this workspace.
                            </p>

                            <div className="billing-context-grid">
                                <div>
                                    <small>Plan</small>
                                    <strong>{currentPlan}</strong>
                                </div>
                                <div>
                                    <small>Status</small>
                                    <strong>
                                        <BillingStatusPill status={subscriptionStatus} />
                                    </strong>
                                </div>
                                <div>
                                    <small>Provider</small>
                                    <strong>{latestSubscription?.provider || "razorpay"}</strong>
                                </div>
                                <div>
                                    <small>Billing account</small>
                                    <strong>{history?.accountName || session.name || "Workspace"}</strong>
                                </div>
                            </div>

                            <SubscriptionCard subscription={latestSubscription} />

                            {!isAgencyClient && (
                                <div className="billing-upgrade-box">
                                    <label>
                                        <span>Choose Plan</span>
                                        <select
                                            value={selectedPlan}
                                            onChange={(e) => setSelectedPlan(e.target.value)}
                                        >
                                            {PLAN_OPTIONS.map((plan) => (
                                                <option key={plan.key} value={plan.key}>
                                                    {plan.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>

                                    <div className="billing-upgrade-actions">
                                        <button
                                            type="button"
                                            className="project-action-btn primary"
                                            onClick={handleCheckout}
                                            disabled={checkoutLoading}
                                        >
                                            {checkoutLoading ? "Starting Razorpay..." : "Start Razorpay Checkout"}
                                        </button>

                                        <button
                                            type="button"
                                            className="project-action-btn secondary"
                                            onClick={handlePortal}
                                            disabled={portalLoading}
                                        >
                                            {portalLoading ? "Opening..." : "Manage Subscription"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="project-panel client-setting-panel">
                            <span className="project-kicker">Invoices</span>
                            <h2>Invoices & Receipts</h2>
                            <p className="settings-muted">
                                Payment and invoice records synced from Razorpay webhooks.
                            </p>

                            {isAgencyClient ? (
                                <div className="billing-empty-card">
                                    <strong>Invoices are managed by agency</strong>
                                    <span>
                                        Contact {invoiceProvider} for invoice downloads or payment receipts.
                                    </span>
                                </div>
                            ) : invoices.length === 0 ? (
                                <div className="billing-empty-card">
                                    <strong>No invoices found yet</strong>
                                    <span>
                                        Razorpay invoice/payment records will appear here after checkout and webhook processing.
                                    </span>
                                </div>
                            ) : (
                                <div className="billing-invoice-list">
                                    {invoices.map((invoice) => (
                                        <InvoiceRow
                                            key={invoice.id || invoice.providerInvoiceId}
                                            invoice={invoice}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <aside className="client-settings-side">
                        <SettingHelp title="Billing owner">
                            {isAgencyClient
                                ? `Billing for this workspace is managed by ${invoiceProvider}.`
                                : "Billing for this workspace is managed by Shriansh Nexus through Razorpay."}
                        </SettingHelp>

                        <SettingHelp title="Razorpay records">
                            Subscriptions, checkout sessions, payments, and invoices are synced from Razorpay webhooks into this workspace.
                        </SettingHelp>

                        <SettingHelp title="Recent activity">
                            {customers.length} customer record(s), {checkoutSessions.length} checkout session(s), and {invoices.length} invoice/payment record(s) found.
                        </SettingHelp>
                    </aside>
                </section>
            </div>
        </PageShell>
    );
}